// The native shell's scanner source. Inside the shell (apps/shell, a Capacitor app around
// this same web app) `window.Capacitor` is injected and the Scanner plugin
// (packages/capacitor-scanner) delivers each read from a dedicated scanner as one event:
// the data with GS intact, the symbology, the device. Nothing is typed, so the keyboard
// layout, the keystroke rate and the GS substitute stop mattering. The wedge listeners
// stay the consumers: the screen on top gets the read, like a keystroke would reach it.

export interface ScannerDevice {
  name: string;
  battery: number | null;
}

export interface ScannerStatus {
  source: string;
  configured: boolean;
  opened: boolean;
  error: string | null;
  devices: ScannerDevice[];
}

interface ScanEvent {
  data: string;
  symbology: string | null;
  symbologyId: number;
  device: string;
}

interface CapPlugin {
  status(): Promise<ScannerStatus>;
  confirm(o: { ok: boolean }): Promise<void>;
  addListener(event: string, fn: (e: never) => void): unknown;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  registerPlugin?: (name: string) => CapPlugin;
  Plugins?: Record<string, CapPlugin | undefined>;
}

const cap = (): CapacitorGlobal | undefined => (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;

/** True inside the native shell. */
export function inShell(): boolean {
  return cap()?.isNativePlatform?.() === true;
}

let plugin: CapPlugin | null = null;
let status: ScannerStatus | null = null;
const readers: Array<(data: string) => void> = [];
const statusSubs = new Set<(s: ScannerStatus) => void>();

/** AIM symbology prefix from the SDK's symbology name, so the scan pipeline tells a GS1-128
 *  label from a plain Code 128 location label the same way it does for the camera and the
 *  keyboard wedge (which sends the prefix itself). Unknown names get no prefix. */
/** Socket Mobile's data-source ids for GS1-128 (the SDK calls it EAN-128). */
const SDK_GS1_128 = new Set([20, 21]);

/** A Code 128 read that the scanner did not flag as GS1-128 but that starts with an SSCC
 *  (AI 00) or a GTIN (AI 01/02) is a GS1 label whose leading FNC1 the scanner dropped
 *  (owner 2026-10-29: the S730 in application mode reports a GS1-128 product label as
 *  Code 128 and Trail answered "code not recognised"). */
export function looksLikeGs1(data: string): boolean {
  return /^(00\d{18}|0[12]\d{14})/.test(data);
}

export function aimPrefix(symbology: string | null, data: string, symbologyId?: number): string {
  if (/^\]\w\w/.test(data)) return "";
  const s = (symbology ?? "").toLowerCase();
  if ((symbologyId !== undefined && SDK_GS1_128.has(symbologyId)) || s.includes("gs1-128") || s.includes("ean-128") || s.includes("ean128") || s.includes("ucc")) return "]C1";
  if (s.includes("128") || symbologyId === 15) return data.startsWith("\u001d") || looksLikeGs1(data) ? "]C1" : "]C0";
  if (s.includes("ean") || s.includes("upc")) return "]E0";
  if (s.includes("data matrix") || s.includes("datamatrix")) return "]d2";
  if (s.includes("qr")) return "]Q3";
  if (s.includes("databar")) return "]e0";
  if (s.includes("39")) return "]A0";
  if (s.includes("2 of 5") || s.includes("2of5") || s.includes("itf")) return "]I0";
  return "";
}

/** The raw string the pipeline expects: prefix, then the data; a GS1-128's leading FNC1
 *  (delivered as GS) goes, the prefix says it. */
export function nativeRaw(e: ScanEvent): string {
  const prefix = aimPrefix(e.symbology, e.data, e.symbologyId);
  const body = prefix === "]C1" && e.data.startsWith("\u001d") ? e.data.slice(1) : e.data;
  return prefix + body;
}

function setStatus(s: ScannerStatus): void {
  status = s;
  for (const fn of statusSubs) fn(s);
}

function plug(): CapPlugin | null {
  if (plugin) return plugin;
  const c = cap();
  if (!c || c.isNativePlatform?.() !== true) return null;
  try {
    plugin = c.Plugins?.["Scanner"] ?? c.registerPlugin?.("Scanner") ?? null;
  } catch {
    plugin = null;
  }
  if (!plugin) return null;
  plugin.addListener("scan", ((e: ScanEvent) => {
    const top = readers[readers.length - 1];
    if (top) top(nativeRaw(e));
  }) as (e: never) => void);
  plugin.addListener("status", ((s: ScannerStatus) => setStatus(s)) as (e: never) => void);
  plugin.status().then(setStatus).catch(() => undefined);
  return plugin;
}

/** Receive reads from the dedicated scanner while the caller's screen is up; the latest
 *  subscriber is the one that gets them. Returns the unsubscribe. */
export function onNativeScan(fn: (data: string) => void): () => void {
  plug();
  readers.push(fn);
  return () => {
    const i = readers.lastIndexOf(fn);
    if (i >= 0) readers.splice(i, 1);
  };
}

/** The scanner's state, or null outside the shell or before the plugin answered. */
export function scannerStatus(): ScannerStatus | null {
  plug();
  return status;
}

export function subscribeScanner(fn: (s: ScannerStatus) => void): () => void {
  plug();
  statusSubs.add(fn);
  if (status) fn(status);
  return () => { statusSubs.delete(fn); };
}

/** Tell the scanner how the read went (its own beep and LED, when it waits for the host). */
export function confirmScan(ok: boolean): void {
  plugin?.confirm({ ok }).catch(() => undefined);
}
